import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input, signal} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, map, merge, Observable, switchMap} from 'rxjs';
import {filter} from 'rxjs/operators';
import {selectBuilding} from 'src/app/state/selectors';
import {BuildingStateSuccess} from 'src/app/state/reducers';
import {ConsumptionAgentService} from '../consumption-agent.service';
import {ActivatedRoute} from '@angular/router';
import {ConsumptionCardComponent} from '../consumption-agents-overview/consumption-card-view/consumption-card/consumption-card.component';
import {filterNulls, TranslatePipe, TranslateService} from '@brunata-angular/util';
import {ConsumptionAgentResult} from '../consumption-agent-result.model';
import {ConsumptionAgentTranslated} from '../consumption-agents-overview/consumption-agent.model';
import {ConsumptionAgentResultTableComponent} from './consumption-agent-result-table/consumption-agent-result-table.component';
import {downloadFile} from '@brunata-angular/util';
import {DatePipe, LoadComponent, NumberPipe} from '@brunata-angular/ui';
import {DateTime} from 'luxon';

type ConsumptionAgentResultView = {
    buildingNo: string;
    agent: ConsumptionAgentTranslated;
    agentResult: ConsumptionAgentResult;
};
@Component({
    selector: 'app-consumption-agent-result',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ConsumptionCardComponent,
        ConsumptionAgentResultTableComponent,
        TranslatePipe,
        LoadComponent,
    ],
    providers: [DatePipe, NumberPipe],
    templateUrl: './consumption-agent-result.component.html',
    styleUrl: './consumption-agent-result.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionAgentResultComponent {
    private readonly store = inject(Store);
    private readonly translatesService = inject(TranslateService);
    private readonly datePipe = inject(DatePipe);
    private readonly numberPipe = inject(NumberPipe);
    private readonly consumptionAgentService = inject(ConsumptionAgentService);
    private readonly activatedRoute = inject(ActivatedRoute);

    protected exportLoading = signal(false);
    protected readonly previewConsumptionAgentRequest$ =
        new BehaviorSubject<ConsumptionAgentTranslated | null>(null);

    @Input() set previewConsumptionAgentRequest(formGroup: ConsumptionAgentTranslated | null) {
        this.previewConsumptionAgentRequest$.next(formGroup);
    }
    private readonly buildingNo$ = this.store.select(selectBuilding).pipe(
        filter((state): state is BuildingStateSuccess => state.status === 'success'),
        map(state => state.buildingNo),
    );

    private readonly reportId$: Observable<string | null> = this.activatedRoute.paramMap.pipe(
        map(params => params.get('alarmid')),
    );

    private readonly consumptionAgent$: Observable<ConsumptionAgentTranslated> = combineLatest([
        this.buildingNo$,
        this.reportId$.pipe(filterNulls()),
    ]).pipe(
        switchMap(([buildingNo, reportId]) =>
            this.consumptionAgentService
                .getConsumptionAgent(buildingNo, reportId)
                .pipe(this.consumptionAgentService.addTranslationsForAgent()),
        ),
    );

    private readonly combinedConsumptionAgent$: Observable<ConsumptionAgentTranslated> = merge(
        this.consumptionAgent$,
        this.previewConsumptionAgentRequest$,
    ).pipe(filterNulls());

    private readonly consumptionAgentResult$: Observable<ConsumptionAgentResult> = combineLatest([
        this.buildingNo$,
        this.combinedConsumptionAgent$,
    ]).pipe(
        switchMap(([buildingNo, agent]) =>
            this.consumptionAgentService.getConsumptionAgentResults(buildingNo, agent).pipe(
                map(agentResult => ({
                    ...agentResult,
                    consumptionToMonitorTranslated: agent.consumptionToMonitorTranslated,
                    investigation: agent.investigation,
                })),
            ),
        ),
    );

    protected readonly view$: Observable<ConsumptionAgentResultView> = combineLatest({
        buildingNo: this.buildingNo$,
        agent: this.combinedConsumptionAgent$,
        agentResult: this.consumptionAgentResult$,
    }).pipe(
        map(({buildingNo, agent, agentResult}) => {
            const finalAgent =
                agentResult.meterCriteriaMismatch === true
                    ? {...agent, meterCriteriaMismatch: agentResult.meterCriteriaMismatch}
                    : agent;
            return {
                buildingNo,
                agent: finalAgent,
                agentResult,
            };
        }),
    );

    protected readonly resultText$: Observable<string> = combineLatest([
        this.combinedConsumptionAgent$,
        this.consumptionAgentResult$,
    ]).pipe(
        map(([agent, agentResult]) =>
            agentResult.isAgentCriteriaFulfilled === true
                ? this.replaceValuesInResultText(
                      this.getResultText(agent, agentResult),
                      agent,
                      agentResult,
                  )
                : this.translatesService.translate(
                      'ReportAgentEmail_sendblankstext',
                      'online',
                      'ReportAgentEmail',
                  ),
        ),
    );
    private translateSummedConsumptionText(
        agent: ConsumptionAgentTranslated,
        agentResult: ConsumptionAgentResult,
    ): string {
        const translationKeyMap: Record<string, string> = {
            HIGHER: 'summedhigher',
            LOWER: 'summedlower',
            EQUAL_TO: 'summedequalto',
            INCREASING:
                agentResult.isAgentCriteriaFulfilled === true && agentResult.diffInPercent === null
                    ? 'summedincreasingwithoutdeviation'
                    : 'summedincreasing',
            DECREASING: 'summeddecreasing',
        };
        return translationKeyMap[agent.operator]
            ? this.translatesService.translate(
                  'ReportAgentEmail_' + translationKeyMap[agent.operator],
                  'online',
                  'ReportAgentEmail',
              )
            : this.translatesService.translate('consumption-agents_default-result-text');
    }
    private translateDifferenceConsumptionText(
        agent: ConsumptionAgentTranslated,
        agentResult: ConsumptionAgentResult,
    ): string {
        const translationKeyMap: Record<string, string> = {
            HIGHER: 'comparegroupshigher',
            LOWER: 'comparegroupslower',
            EQUAL_TO: 'comparegroupsequalto',
            INCREASING:
                agentResult.isAgentCriteriaFulfilled === true && agentResult.diffInPercent === null
                    ? 'comparegroupsincreasingwithoutdeviation'
                    : 'comparegroupsincreasing',
            DECREASING: 'comparegroupsdecreasing',
            HIGHER_THAN_GROUP2:
                agentResult.isAgentCriteriaFulfilled === true && agentResult.diffInPercent === null
                    ? 'comparegroupshigherthangroup2withoutdeviation'
                    : 'comparegroupshigherthangroup2',
            LOWER_THAN_GROUP2: 'comparegroupslowerthangroup2',
        };
        return translationKeyMap[agent.operator]
            ? this.translatesService.translate(
                  'ReportAgentEmail_' + translationKeyMap[agent.operator],
                  'online',
                  'ReportAgentEmail',
              )
            : this.translatesService.translate('consumption-agents_default-result-text');
    }
    private getResultText(
        agent: ConsumptionAgentTranslated,
        agentResult: ConsumptionAgentResult,
    ): string {
        switch (agent.consumptionToMonitor) {
            case 'SUMMEDCONSUMPTION':
                return this.translateSummedConsumptionText(agent, agentResult);
            case 'DIFFERENCECONSUMPTION':
                return this.translateDifferenceConsumptionText(agent, agentResult);
            case 'INDIVIDUALCONSUMPTION':
                return this.translatesService.translate('consumption-agents_default-result-text');
        }
    }
    private replaceNumericPlaceholders(
        translatedText: string,
        replacements: Record<string, number | null>,
    ): string {
        return Object.entries(replacements).reduce((text, [placeholder, value]) => {
            const formattedValue = this.numberPipe.transform(value);
            return text.replaceAll(`{{${placeholder}}}`, formattedValue);
        }, translatedText);
    }

    private replaceDatePlaceholders(
        translatedText: string,
        replacements: Record<string, DateTime | null>,
    ): string {
        return Object.entries(replacements).reduce((text, [placeholder, value]) => {
            const formattedValues = this.datePipe.transform(value);
            return text.replaceAll(`{{${placeholder}}}`, formattedValues);
        }, translatedText);
    }
    private replaceValuesInResultText(
        translatedText: string,
        agent: ConsumptionAgentTranslated,
        agentResult: ConsumptionAgentResult,
    ): string {
        // Replace group names
        translatedText = translatedText
            .replace('{{groupName1}}', agent.meterSettings.meterGroup.groupName)
            .replace('{{groupName2}}', agent.meterSettings.meterGroup2.groupName);

        // Insert dates
        translatedText = this.replaceDatePlaceholders(translatedText, {
            periodStartDate: agentResult.monitoringPeriod?.startDate ?? null,
            periodEndDate: agentResult.monitoringPeriod?.endDate ?? null,
            monitoringPeriodStartDate: agentResult.monitoringPeriod?.startDate ?? null,
            monitoringPeriodEndDate: agentResult.monitoringPeriod?.endDate ?? null,
            comparisonPeriodStartDate: agentResult.comparisonPeriod?.startDate ?? null,
            comparisonPeriodEndDate: agentResult.comparisonPeriod?.endDate ?? null,
        });
        // Insert with translation
        translatedText = translatedText.replaceAll(
            '{{unit}}',
            this.translatesService.translate(
                'mappers_measurementUnit.' + agent.meterSettings.outputUnit,
                'online',
                'common',
            ),
        );
        // Insert with number pipe
        translatedText = this.replaceNumericPlaceholders(translatedText, {
            consumption: agentResult.summedMonitoringConsumption ?? null,
            fixedValue: agentResult.fixedValue ?? null,
            monitoringConsumption: agentResult.summedMonitoringConsumption ?? null,
            percentualDeviation: Math.abs(agentResult.diffInPercent ?? 0),
            reportPercentage: Math.abs(agent.percentage ?? 0),
            comparisonConsumption:
                agentResult.comparisonConsumption ??
                agentResult.summedComparisonConsumption ??
                null,
            group1Consumption: agentResult.summedMonitoringConsumptionGroup1 ?? null,
            group2Consumption: agentResult.summedMonitoringConsumptionGroup2 ?? null,
        });

        return translatedText;
    }
    protected downloadFile(buildingNo: string, consumptionAgent: ConsumptionAgentTranslated): void {
        this.exportLoading.set(true);
        this.consumptionAgentService
            .exportConsumptionAgentResults(buildingNo, consumptionAgent)
            .subscribe(({fileName, blob}) => {
                downloadFile(fileName, blob);
                this.exportLoading.set(false);
            });
    }
}
